import {
  WebTracerProvider,
} from '@opentelemetry/sdk-trace-web';
import {
  type SpanContext,
  SpanStatusCode,
} from '@opentelemetry/api';
import {
  faro,
} from '#src/helpers/otel/faro';

const otel = faro.api.getOTEL();
const {
  trace,
  context,
} = otel!;
export const provider = new WebTracerProvider({});

/**
 * Function to wrap another function with an OpenTelemetry trace span.
 *
 * @export
 * @template T                    Type of "args" parameter which is used to call "func(...args);"
 * @template F                    Type of "func" parameter
 * @param {F} func                Function to wrap with a trace span
 * @param {T[]} [args=[]]         Arguments list which is used to call "func(...args);"
 * @param {string} [spanName=""]  Name for the trace span. Defaults to the name of "func" if not set
 * @return {*}  {ReturnType<F>}   The value returned from "func(...args);"
 */
export async function traceSpan<
  F extends (...args: T[]) => PromiseLike<ReturnType<F>> | ReturnType<F>,
  T,
>(
  func: F,
  args: T[] = [],
  spanName: string = '',
  funcContextPropagator?: (result: ReturnType<F>) => PromiseLike<SpanContext> | SpanContext,
): Promise<ReturnType<F>> {
  if (spanName.length === 0) {
    spanName = func.name;
  }
  const tracer = trace.getTracer('default');
  const singleSpan = tracer.startSpan(spanName);
  return context.with(
    trace.setSpan(
      context.active(),
      singleSpan,
    ),
    async () => {
      try {
        if (funcContextPropagator) { // Await the result of 'func(...args)' if funcContextPropagator is set.
          const result = await func(
            ...args,
          );
          const funcContext = await funcContextPropagator(result);
          singleSpan.addLink({
            context: funcContext,
          });
          singleSpan.end();
          return result;
        } else { // If funcContextPropagator is not set, return the result without awaiting async results.
          const result = func(
            ...args,
          );
          singleSpan.end();
          return result;
        }
      } catch (err: unknown) {
        singleSpan.setStatus({
          code: SpanStatusCode.ERROR,
          message: JSON.stringify(err, null, 2),
        });
        singleSpan.end();
        throw err;
      }
    },
  );
}

export default provider;
